import { Injectable } from 'framework/di';
import { pipe } from 'framework/functional';

import {
  RecommendationPayload,
  RecommendationQueryParams,
  RecommendationResponse,
} from '../../providers/boost';
import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { B2BService } from '../b2b/b2b.service';
import { PlatformLoader } from '../platform/platform.loader';

/**
 * API service for handling recommendation widget requests.
 */
@Injectable
export class RecommendationAPI extends BoostAPI {
  constructor(
    platformLoader: PlatformLoader,
    private appService: AppService,
    private b2bService: B2BService
  ) {
    super(platformLoader);
    this.config.baseURL = this.appService.TAEAppConfig.recommendUrl;
  }

  /**
   * Builds query parameters for recommendation requests.
   *
   * This method can be overridden to add shop-specific query parameters
   * such as custom pricing tiers, customer segments, or feature flags.
   *
   * @param params - Base recommendation query parameters
   * @returns Enhanced query parameters with currency and B2B settings
   *
   * @example
   * // Override to add custom parameters
   * protected buildRecommendationQueryParams(params) {
   *   const enhancedParams = super.buildRecommendationQueryParams(params);
   *   const customer = this.platformLoader.platform.customer;
   *   if (customer?.tags?.includes('wholesale')) {
   *     enhancedParams.pricing_tier = 'wholesale';
   *   }
   *   return enhancedParams;
   * }
   */
  protected buildRecommendationQueryParams(
    params: RecommendationQueryParams
  ): RecommendationQueryParams {
    return pipe(
      params,
      (p) => this.addCurrencyParams(p),
      (p) => this.b2bService.addB2BRequestParams(p)
    );
  }

  /**
   * Builds the body payload for recommendation requests.
   *
   * This method can be overridden to modify or enhance the request body
   * with shop-specific data, custom filters, or additional context.
   *
   * @param bodyPayload - Base recommendation request payload
   * @returns Enhanced body payload
   *
   * @example
   * // Override to add custom body data
   * protected buildRecommendationBodyPayload(bodyPayload) {
   *   const enhancedBody = super.buildRecommendationBodyPayload(bodyPayload);
   *   enhancedBody.customFilters = {
   *     excludeTags: ['out-of-stock', 'discontinued']
   *   };
   *   return enhancedBody;
   * }
   */
  protected buildRecommendationBodyPayload(
    bodyPayload: RecommendationPayload
  ): RecommendationPayload {
    // Allow extension point for body payload modifications
    return bodyPayload;
  }

  /**
   * Fetches recommendation products from the API.
   *
   * This method orchestrates the recommendation request by building query parameters
   * and body payload through extensible protected methods, then making the API call.
   *
   * @param params - Query parameters for the recommendation request
   * @param bodyPayload - Body payload containing request data
   * @returns Promise resolving to recommendation response with products
   */
  getRecommendation(params: RecommendationQueryParams, bodyPayload: RecommendationPayload) {
    const enhancedParams = this.buildRecommendationQueryParams(params);
    const enhancedBody = this.buildRecommendationBodyPayload(bodyPayload);
    const queryParams = this.buildDefaultQueryParams(enhancedParams);
    return this.post<RecommendationResponse>(`/?${queryParams}`, enhancedBody);
  }
}
